import React, { useState, useEffect } from "react";
import styles from "../styles/CreatorDashboardNew.module.css";
import { draftBlocksData, broadcastedBlocksData } from "../data/dummyBlocks4";
import sha256 from "crypto-js/sha256";
import { dummyBlocks } from "../data/dummyBlocks";

export default function CreatorDashboard() {
  const [activeTab, setActiveTab] = useState("drafts"); // drafts | broadcasted
  const [viewMode, setViewMode] = useState("table"); // card | table
  const [drafts, setDrafts] = useState(draftBlocksData);
  const [broadcasted, setBroadcasted] = useState(broadcastedBlocksData);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(null);
  const [showCreate, setShowCreate] = useState(false);


  const [formData, setFormData] = useState({
    step: 0,   // 👈 add this
    ownerName: "",
    plotNo: "",
    location: "",
    areaSqm: "",
    description: ""
  });


  // Simulate pending broadcast verification
  useEffect(() => {
    const id = setInterval(() => {
      setBroadcasted((prev) => {
        const idx = prev.findIndex((b) => b.status === "pending");
        if (idx === -1) return prev;
        const copy = [...prev];
        copy[idx] = { ...copy[idx], status: "verified" };
        return copy;
      });
    }, 16000);
    return () => clearInterval(id);
  }, []);

  const openDetails = (block) => setShowModal(block);
  const closeModal = () => setShowModal(null);

  const createBlock = (payload) => {
    const lastAll = [...drafts, ...broadcasted].slice(-1)[0];
    const index = (lastAll ? lastAll.index : 0) + 1;
    const newBlock = {
      blockID: `BLOCK-${String(index).padStart(4, "0")}`,
      index,
      timestamp: new Date().toISOString(),
      hash: sha256(Object.values(payload).join("")).toString(),
      prevHash: lastAll ? lastAll.hash : "0000000000",
      status: "draft",
      payload,
    };
    setDrafts((s) => [newBlock, ...s]);
    setShowCreate(false);
    setFormData({ ownerName: "", plotNo: "", location: "", areaSqm: "", description: "" });
  };


  const broadcastBlock = (blockID) => {
    const block = drafts.find((d) => d.blockID === blockID);
    if (!block) return;
    const moved = { ...block, status: "pending" };
    setBroadcasted((s) => [moved, ...s]);
    setDrafts((s) => s.filter((d) => d.blockID !== blockID));
    setShowModal({ ...moved, justBroadcasted: true });
  };

  const filteredBroadcasted = broadcasted.filter((b) =>
    filter === "all" ? true : b.status === filter
  );
  const currentBlocks = activeTab === "drafts" ? drafts : filteredBroadcasted;

  // Calculate dynamic hash progress (percentage of filled fields)
  const hashProgress = Math.round(
    (Object.values(formData).filter((v) => v && v.toString().trim() !== "").length / Object.keys(formData).length) * 100
  );



  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Town Planning — Blockchain Creator</h1>
          <p className={styles.subtitle}>PMRDA / Municipal Ledger — Block Creator Console</p>
        </div>
        <div>
          <button className={styles.primaryBtn} onClick={() => setShowCreate(true)}>+ Create Block</button>
        </div>
      </header>

      {/* Tab Switch */}
      <div className={styles.toggleBar}>
        <button
          className={`${styles.toggleBtn} ${activeTab === "drafts" ? styles.active : ""}`}
          onClick={() => setActiveTab("drafts")}
        >
          Drafted Blocks ({drafts.length})
        </button>
        <button
          className={`${styles.toggleBtn} ${activeTab === "broadcasted" ? styles.active : ""}`}
          onClick={() => setActiveTab("broadcasted")}
        >
          Broadcasted / Validated ({broadcasted.length})
        </button>
      </div>

      {/* View & Filter */}
      <div className={styles.viewControls}>
        <label>View Mode:</label>
        <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
          <option value="table">Table View</option>
          <option value="card">Card View</option>
        </select>

        {activeTab === "broadcasted" && (
          <>
            <label>Status:</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="not_verified">Not Verified</option>
            </select>
          </>
        )}
      </div>

      {/* Content */}
      {viewMode === "table" ? (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Block ID</th>
              <th>Owner</th>
              <th>Plot</th>
              <th>Location</th>
              <th>Area (sqm)</th>
              <th>Timestamp</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentBlocks.length === 0 && (
              <tr>
                <td colSpan="8" className={styles.emptyRow}>No records found</td>
              </tr>
            )}
            {currentBlocks.map((b) => (
              <tr key={b.blockID}>
                <td>{b.blockID}</td>
                <td>{b.payload.ownerName}</td>
                <td>{b.payload.plotNo}</td>
                <td>{b.payload.location}</td>
                <td>{b.payload.areaSqm}</td>
                <td>{new Date(b.timestamp).toLocaleString()}</td>
                <td><span className={`${styles.statusPill} ${styles[b.status]}`}>{b.status.toUpperCase()}</span></td>
                <td>
                  <button className={styles.detailsBtn} onClick={() => openDetails(b)}>Details</button>
                  {b.status === "draft" && <button className={styles.validateBtn} onClick={() => broadcastBlock(b.blockID)}>Broadcast</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className={styles.cardList}>
          {currentBlocks.map((b) => (
            <div key={b.blockID} className={styles.blockCard}>
              <div className={styles.cardTop}>
                <div><strong>{b.blockID}</strong> <span className={`${styles.statusPill} ${styles[b.status]}`}>{b.status.toUpperCase()}</span></div>
                <div>{new Date(b.timestamp).toLocaleString()}</div>
              </div>
              <div className={styles.cardBody}>
                <p><strong>Owner:</strong> {b.payload.ownerName}</p>
                <p><strong>Plot:</strong> {b.payload.plotNo}</p>
                <p><strong>Location:</strong> {b.payload.location}</p>
                <p><strong>Area:</strong> {b.payload.areaSqm} sqm</p>
              </div>
              <div className={styles.cardActions}>
                <button className={styles.detailsBtn} onClick={() => openDetails(b)}>View</button>
                {b.status === "draft" && <button className={styles.validateBtn} onClick={() => broadcastBlock(b.blockID)}>Broadcast</button>}
              </div>
            </div>
          ))}
        </div>
      )}





      {showCreate && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Create New Block</h3>

            {/* Stepper Progress */}
            <div className={styles.stepper}>
              {["Main TDR", "Utilizer", "Header", "Review"].map((step, i) => (
                <div
                  key={i}
                  className={`${styles.step} ${i <= (formData.step || 0) ? styles.activeStep : ""}`}
                >
                  <span>{i + 1}</span>
                  <p>{step}</p>
                </div>
              ))}
            </div>

            {/* Step Content */}
            <form
              className={styles.createForm}
              onSubmit={(e) => { e.preventDefault(); createBlock(formData); }}
            >
              {/* Step 1: Holder */}
              {formData.step === 0 && (
                <fieldset className={styles.section}>
                  <legend>Holder Details</legend>

                  <div className={styles.formRow}>
                    <label>Holder Name</label>
                    <input
                      type="text"
                      value={formData.holderName || ""}
                      onChange={(e) => setFormData({ ...formData, holderName: e.target.value })}
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label>Survey No</label>
                    <input
                      type="text"
                      value={formData.holderSurvey || ""}
                      onChange={(e) => setFormData({ ...formData, holderSurvey: e.target.value })}
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label>Village / City</label>
                    <input
                      type="text"
                      value={formData.holderVillage || ""}
                      onChange={(e) => setFormData({ ...formData, holderVillage: e.target.value })}
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label>District</label>
                    <input
                      type="text"
                      value={formData.holderDistrict || ""}
                      onChange={(e) => setFormData({ ...formData, holderDistrict: e.target.value })}
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label>FSI Granted</label>
                    <input
                      type="text"
                      value={formData.fsiGranted || ""}
                      onChange={(e) => setFormData({ ...formData, fsiGranted: e.target.value })}
                    />
                  </div>


                  {/* New Upload Field */}
                  <div className={styles.formRow}>
                    <label>Upload Document</label>
                    <input
                      type="file"
                      onChange={(e) =>
                        setFormData({ ...formData, holderDocument: e.target.files[0] })
                      }
                    />
                  </div>

                </fieldset>
              )}



              {/* Step 2: Utilizer */}
              {formData.step === 1 && (
                <fieldset className={styles.section}>
                  <legend>Utilizer Details</legend>

                  {/* Utilisation History Table */}
                  {(() => {
                    const holderBlock = dummyBlocks.find(
                      (blk) => blk.payload.MainTDR.holderName === formData.holderName
                    );
                    return holderBlock && holderBlock.payload.utilisationHistory.length > 0 ? (
                      <div className={styles.utilizerHistory}>
                        <h4>Previous Utilisation History</h4>
                        <table className={styles.historyTable}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Utilizer</th>
                              <th>Used At</th>
                              <th>Survey No</th>
                              <th>Area Used (sqm)</th>
                              <th>Balance Area (sqm)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {holderBlock.payload.utilisationHistory.map((u, idx) => (
                              <tr key={idx}>
                                <td>{u.date}</td>
                                <td>{u.utiliserName}</td>
                                <td>{u.usedAt}</td>
                                <td>{u.surveyNo}</td>
                                <td>{u.areaUtilizedSqm}</td>
                                <td>{u.balanceAreaSqm}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className={styles.noHistory}>No previous utilisation records found.</p>
                    );
                  })()}

                  {/* New Utilizer Form */}
                  <div className={styles.newUtilizerForm}>
                    <h4>Add New Utilizer</h4>
                    <div className={styles.formRow}>
                      <label>Utilizer Name</label>
                      <input
                        type="text"
                        value={formData.utilizerName || ""}
                        onChange={(e) => setFormData({ ...formData, utilizerName: e.target.value })}
                      />
                    </div>
                    <div className={styles.formRow}>
                      <label>Used Land Survey No</label>
                      <input
                        type="text"
                        value={formData.usedSurvey || ""}
                        onChange={(e) => setFormData({ ...formData, usedSurvey: e.target.value })}
                      />
                    </div>
                    <div className={styles.formRow}>
                      <label>Village / City</label>
                      <input
                        type="text"
                        value={formData.usedVillage || ""}
                        onChange={(e) => setFormData({ ...formData, usedVillage: e.target.value })}
                      />
                    </div>
                    <div className={styles.formRow}>
                      <label>Area to be Used</label>
                      <input
                        type="number"
                        value={formData.areaUsed || ""}
                        onChange={(e) => setFormData({ ...formData, areaUsed: e.target.value })}
                      />
                    </div>
                  </div>
                </fieldset>
              )}


              {/* Step 3: Header */}
              {/* Step 3: Header */}
              {formData.step === 2 && (
                <fieldset className={styles.section}>
                  <legend>Block Header</legend>

                  <div className={styles.formRow}>
                    <label>Block Index</label>
                    <input
                      type="number"
                      value={formData.index || 1}   // auto-filled
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label>Timestamp</label>
                    <input
                      type="text"
                      value={formData.timestamp || new Date().toLocaleString()} // auto-filled
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label>Previous Hash</label>
                    <input
                      type="text"
                      value={formData.prevHash || "Auto-generated"} // auto-filled
                      readOnly
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label>Creator Signature</label>
                    <input
                      type="text"
                      value={formData.creatorSignature || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, creatorSignature: e.target.value })
                      }
                    />
                  </div>
                </fieldset>
              )}



              {/* Step 4: Review */}
              {/* Step 4: Review */}
              {formData.step === 3 && (
                <fieldset className={styles.section}>
                  <legend>Review Block</legend>

                  <div className={styles.reviewBox}>
                    {/* Holder Details */}
                    <h4>Holder Details</h4>
                    <table className={styles.reviewTable}>
                      <tbody>
                        <tr><td>Holder Name</td><td>{formData.holderName}</td></tr>
                        <tr><td>Village / City</td><td>{formData.holderVillage}</td></tr>
                        <tr><td>Survey No</td><td>{formData.holderSurvey}</td></tr>
                        <tr><td>District</td><td>{formData.holderDistrict}</td></tr>
                        <tr><td>FSI Granted</td><td>{formData.fsiGranted}</td></tr>
                        <tr><td>Current Document Hash</td><td>{formData.currentDocHash || "Auto-generated"}</td></tr>
                        <tr><td>Previous Document Hash</td><td>{formData.prevDocHash || "N/A"}</td></tr>
                      </tbody>
                    </table>

                    {/* Utilizer Details */}
                    <h4>Utilizer Details</h4>
                    <table className={styles.reviewTable}>
                      <tbody>
                        <tr><td>Utilizer Name</td><td>{formData.utilizerName}</td></tr>
                        <tr><td>Survey No</td><td>{formData.usedSurvey}</td></tr>
                        <tr><td>Village / City</td><td>{formData.usedVillage}</td></tr>
                        <tr><td>District</td><td>{formData.utilizerDistrict || "N/A"}</td></tr>
                        <tr><td>State</td><td>{formData.utilizerState || "N/A"}</td></tr>
                        <tr><td>Area Used</td><td>{formData.areaUsed} sqm</td></tr>
                        <tr>
                          <td>Balanced Area</td>
                          <td>
                            {formData.prevBalanceArea && formData.areaUsed
                              ? formData.prevBalanceArea - formData.areaUsed
                              : "N/A"} sqm
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Block Header */}
                    <h4>Block Header</h4>
                    <table className={styles.reviewTable}>
                      <tbody>
                        <tr><td>Index</td><td>{formData.index}</td></tr>
                        <tr><td>Timestamp</td><td>{formData.timestamp}</td></tr>
                        <tr><td>Previous Hash</td><td>{formData.prevHash}</td></tr>
                        <tr><td>Current Block Hash</td><td>{formData.blockHash || "Auto-generated"}</td></tr>
                        <tr><td>Creator Signature</td><td>{formData.creatorSignature}</td></tr>
                      </tbody>
                    </table>

                    {/* Validators */}
                    <h4>Validator Signatures</h4>
                    <table className={styles.reviewTable}>
                      <tbody>
                        {formData.validatorSignatures && formData.validatorSignatures.length > 0 ? (
                          formData.validatorSignatures.map((sig, idx) => (
                            <tr key={idx}>
                              <td>Validator {idx + 1}</td>
                              <td>{sig}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="2">No validator signatures yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </fieldset>
              )}



              {/* Navigation */}
              <div className={styles.modalActions}>
                {formData.step > 0 && (
                  <button type="button" className={styles.closeBtn}
                    onClick={() => setFormData({ ...formData, step: formData.step - 1 })}>
                    Back
                  </button>
                )}
                {formData.step < 4 ? (
                  <button type="button" className={styles.primaryBtn}
                    onClick={() => setFormData({ ...formData, step: (formData.step || 0) + 1 })}>
                    Next
                  </button>
                ) : (
                  <button type="submit" className={styles.primaryBtn}>Create Block</button>
                )}
                <button type="button" className={styles.closeBtn} onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Details Modal */}
{/* Details Modal */}
{showModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modal}>
      <h3>{showModal.blockID} — Block Details</h3>

      <div className={styles.detailsContent}>
        {/* Block Header */}
        <h4>Block Header</h4>
        <div className={styles.twoColGrid}>
          <div><strong>Index:</strong> {showModal.index}</div>
          <div><strong>Block ID:</strong> {showModal.blockID}</div>
          <div><strong>Timestamp:</strong> {new Date(showModal.timestamp).toLocaleString()}</div>
          <div><strong>Previous Hash:</strong> {showModal.previousHash ? showModal.previousHash.substring(0, 15) : ""}...</div>
          <div><strong>Merkle Root:</strong> {showModal.merkleRoot ? showModal.merkleRoot.substring(0, 15) : ""}...</div>
          <div><strong>Hash:</strong> {showModal.hash ? showModal.hash.substring(0, 15) : ""}...</div>
          <div><strong>Status:</strong> 
            <span className={`${styles.statusPill} ${styles[showModal.status]}`}>
              {showModal.status?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Main TDR */}
        <h4>Main TDR</h4>
        <div className={styles.twoColGrid}>
          <div><strong>Holder Name:</strong> {showModal.payload.MainTDR.holderName}</div>
          <div><strong>Survey No:</strong> {showModal.payload.MainTDR.surveyNo}</div>
          <div><strong>Village / City:</strong> {showModal.payload.MainTDR.villageCity}</div>
          <div><strong>District:</strong> {showModal.payload.MainTDR.district}</div>
          <div><strong>State:</strong> {showModal.payload.MainTDR.state}</div>
          <div><strong>FSI Credit (sqm):</strong> {showModal.payload.MainTDR.fsiCreditSqm}</div>
          <div className={styles.fullWidth}><strong>Document Hash:</strong> {showModal.payload.MainTDR.documentHash}</div>
        </div>

        {/* Utilisation History */}
        <h4>Utilisation History</h4>
        {showModal.payload.utilisationHistory?.length > 0 ? (
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Utilizer</th>
                <th>Used At</th>
                <th>Survey No</th>
                <th>Area Used (sqm)</th>
                <th>Balance Area (sqm)</th>
                <th>Document Hash</th>
              </tr>
            </thead>
            <tbody>
              {showModal.payload.utilisationHistory.map((u, idx) => (
                <tr key={idx}>
                  <td>{u.date}</td>
                  <td>{u.utiliserName}</td>
                  <td>{`${u.usedAt}, ${u.usedAtDistrict}, ${u.usedAtState}`}</td>
                  <td>{u.surveyNo}</td>
                  <td>{u.areaUtilizedSqm}</td>
                  <td>{u.balanceAreaSqm}</td>
                  <td>{u.documentHash? u.documentHash.substring(0, 15) : ""}...</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No utilisation history available.</p>
        )}

        {/* Documents */}
        <h4>Documents</h4>
        <div className={styles.twoColGrid}>
          <div><strong>Current TDR:</strong> <a href={showModal.documents.currentTDR} target="_blank" rel="noreferrer">View Document</a></div>
          <div><strong>Previous TDR:</strong> <a href={showModal.documents.previousTDR} target="_blank" rel="noreferrer">View Document</a></div>
        </div>

        {/* Validators */}
        <h4>Validator Signatures</h4>
        {showModal.validatorsSignature?.length > 0 ? (
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th>Validator ID</th>
                <th>Signature</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {showModal.validatorsSignature.map((v, idx) => (
                <tr key={idx}>
                  <td>{v.validatorID}</td>
                  <td>{v.signature}</td>
                  <td>{new Date(v.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No validator signatures.</p>
        )}

        {/* Meta */}
        <h4>Meta</h4>
        <div className={styles.twoColGrid}>
          <div><strong>Created By:</strong> {showModal.payload.meta.createdBy}</div>
        </div>
      </div>

      <div className={styles.modalActions}>
        {showModal.status === "draft" && (
          <button
            className={styles.validateBtn}
            onClick={() => broadcastBlock(showModal.blockID)}
          >
            Broadcast
          </button>
        )}
        <button className={styles.closeBtn} onClick={closeModal}>Close</button>
      </div>
    </div>
  </div>
)}


    </div>
  );
}
